import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import { designTokens } from "@itandi/itandi-bb-ui";
import { Alert } from "@itandi/itandi-bb-ui/Alert";
import { Box } from "@itandi/itandi-bb-ui/Box";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Dropdown } from "@itandi/itandi-bb-ui/Dropdown";
import { DropdownMenu } from "@itandi/itandi-bb-ui/DropdownMenu";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { PresentationChip } from "@itandi/itandi-bb-ui/PresentationChip";
import { Table } from "@itandi/itandi-bb-ui/Table";
import { Tbody } from "@itandi/itandi-bb-ui/Tbody";
import { Td } from "@itandi/itandi-bb-ui/Td";
import { Th } from "@itandi/itandi-bb-ui/Th";
import { Thead } from "@itandi/itandi-bb-ui/Thead";
import { Tr } from "@itandi/itandi-bb-ui/Tr";
import type { FC } from "react";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";
import { LineUserAccountModal } from "~/components/common/line_user_accounts/LineUserAccountModal";
import { renderDropDownButton } from "~/components/common/parts/dropdown/DropDownButton";
import type { LineUserAccount } from "~/models/LineUserAccount";
import { isSuccess } from "~/utils/api/api_handler";
import { destroy } from "~/utils/api/vendors/LineUserAccounts";
import { generatePath } from "~/utils/generatePath";

type Props = DeepReadonly<{
	lineUserAccounts: LineUserAccount[];
}>;

export const LineUserAccountList: FC<Props> = ({ lineUserAccounts }: Props) => {
	const [editingAccount, setEditingAccount] = useState<LineUserAccount | null>(
		null
	);
	const [deletingAccount, setDeletingAccount] =
		useState<LineUserAccount | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { mutate: globalMutate } = useSWRConfig();

	const handleDestroySubmit = useCallback(async () => {
		const result = await destroy(deletingAccount!.id);
		setIsSubmitting(true);
		if (isSuccess(result)) {
			toast.success("削除しました。");
			await globalMutate(generatePath("/vendors/line_user_accounts"));
			setDeletingAccount(null);
		} else {
			toast.error(`エラーが発生しました。${result.message}`);
		}
		setIsSubmitting(false);
	}, [deletingAccount, globalMutate]);

	return (
		<>
			<Table>
				<Thead>
					<Tr>
						<Th>名前</Th>
						<Th width="6rem">操作</Th>
					</Tr>
				</Thead>
				<Tbody>
					{lineUserAccounts.map((lineUserAccount) => (
						<Tr key={lineUserAccount.id}>
							<Td>
								<Flex gap={designTokens["spacing/xs"]}>
									<Box>{lineUserAccount.name}</Box>
									{lineUserAccount.registered ? (
										<PresentationChip variant="gray">
											通知設定登録済み
										</PresentationChip>
									) : null}
								</Flex>
							</Td>
							<Td>
								<Dropdown button={renderDropDownButton("操作")}>
									<DropdownMenu
										onClick={() => {
											setEditingAccount(lineUserAccount);
										}}
									>
										編集
									</DropdownMenu>
									<DropdownMenu
										danger
										disabled={lineUserAccount.registered}
										onClick={() => {
											setDeletingAccount(lineUserAccount);
										}}
									>
										削除
									</DropdownMenu>
								</Dropdown>
							</Td>
						</Tr>
					))}
				</Tbody>
			</Table>
			{editingAccount ? (
				<LineUserAccountModal
					handleClose={() => {
						setEditingAccount(null);
					}}
					lineUserAccount={editingAccount}
				/>
			) : null}
			{deletingAccount ? (
				<Modalv2
					isOpen
					onClose={() => {
						setDeletingAccount(null);
					}}
					size="s"
				>
					<ModalHeader title="通知設定の削除" />
					<ModalBody>
						「{deletingAccount.name}」を削除します。
						<Alert mt={designTokens["spacing/m"]} variant="warning">
							この操作は元に戻せません。
						</Alert>
					</ModalBody>
					<ModalFooter
						leftArea={
							<Button
								onClick={() => {
									setDeletingAccount(null);
								}}
								variant="secondary"
							>
								キャンセル
							</Button>
						}
						rightArea={
							<Button
								loading={isSubmitting}
								onClick={handleDestroySubmit}
								variant="danger"
							>
								削除
							</Button>
						}
					/>
				</Modalv2>
			) : null}
		</>
	);
};
